import React, { useState, useEffect } from 'react'
import axios from 'axios'
import moment, { Moment } from 'moment-timezone'
import { DragDropContext, DropResult } from '@hello-pangea/dnd'
import { merge_api_internal_menus_path } from '../../../routes'
import SectionTitle from '../shared/sectionTitle'
import FlashNotification from '../shared/flashNotification'
import WeekPicker from './WeekPicker'
import WeekView from './WeekView'
import RecipeList from './RecipeList'
import MenuList from './MenuList'
import MenuSearchBar from './MenuSearchBar'
import {
  LocalizationContext,
  LocalizationContextType,
  DEFAULT_LOCALIZATION_CONTEXT
} from '../shared/contexts'

export interface DroppedRecipe {
  id: number
  recipe_id: number
  name: string
  image_url: string
  quantity: number
  position: number
}

export interface MenuRecipe {
  id: number
  menu_id: number
  recipe_id: number
  recipe_name: string
  image_url: string
  amount: number
}

export interface Menu {
  id: number
  date: string
  menu_recipes: MenuRecipe[]
}

interface NewMenuProps {
  localization_context?: LocalizationContextType
  canEdit: boolean
}

const NewMenu: React.FC<NewMenuProps> = ({ localization_context, canEdit }) => {
  // "today" must mean the tenant's today, not the browser's. A remote admin in
  // a different zone managing this kitchen needs the calendar/highlight to
  // anchor on the location's wall-clock day.
  const tenantTz = localization_context?.timezone || 'UTC'

  const getInitialDate = (): Moment => {
    const urlParams = new URLSearchParams(window.location.search)
    const dateParam = urlParams.get('date')
    if (dateParam) {
      const parsedDate = moment.tz(dateParam, 'YYYY-MM-DD', tenantTz)
      if (parsedDate.isValid()) {
        return parsedDate
      }
    }
    return moment.tz(tenantTz)
  }

  const [selectedDate, setSelectedDate] = useState<Moment>(getInitialDate())
  const [selectedWeekDay, setSelectedWeekDay] =
    useState<Moment>(getInitialDate())
  const [recipesSearchQuery, setRecipesSearchQuery] = useState<string>('')
  const [menuSearchQuery, setMenuSearchQuery] = useState<string>('')
  const [droppedRecipes, setDroppedRecipes] = useState<DroppedRecipe[]>([])
  const [menus, setMenus] = useState<Menu[]>([])
  const [menuFetchMonth, setMenuFetchMonth] = useState(
    getInitialDate().format('MM')
  )
  const [copiedMenuItems, setCopiedMenuItems] = useState<Array<{
    recipe_id: number
    name: string
    image_url: string
    quantity: number
  }> | null>(null)
  const [fixedItemQuantityOverrides, setFixedItemQuantityOverrides] = useState<
    Record<number, number>
  >({})
  const [removedFixedItemIds, setRemovedFixedItemIds] = useState<number[]>([])
  const [loading, setLoading] = useState(false)
  const [menuLoading, setMenuLoading] = useState(true)
  const [flashMessage, setFlashMessage] = useState<{
    type: 'success' | 'danger'
    message: string
  } | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const confirmNavigationIfUnsaved = (): boolean => {
    if (hasUnsavedChanges) {
      return window.confirm(
        'Changes on menu for selected date have not been saved. Proceed?'
      )
    }
    return true
  }

  const resetChangesState = () => {
    setDroppedRecipes([])
    setFixedItemQuantityOverrides({})
    setRemovedFixedItemIds([])
    setHasUnsavedChanges(false)
  }

  // The menu page is server-rendered (no client router), so leaving it — a navbar click,
  // reload, tab close, or manual URL change — is a full-page navigation. Warn via the
  // browser's native beforeunload prompt whenever there are unsaved changes. (Day/week
  // switches within the page are handled separately by confirmNavigationIfUnsaved.)
  useEffect(() => {
    if (!hasUnsavedChanges) return

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [hasUnsavedChanges])

  const fetchMenu = () => {
    setMenuLoading(true)
    return axios
      .get('/menu.json', {
        params: {
          month: selectedWeekDay?.format('YYYY-MM')
        }
      })
      .then((response) => {
        setMenus(response.data)
      })
      .finally(() => {
        setMenuLoading(false)
      })
  }

  useEffect(() => {
    if (selectedWeekDay.format('MM') !== menuFetchMonth) {
      setMenuFetchMonth(selectedWeekDay?.format('MM'))
    }
    fetchMenu()
  }, [menuFetchMonth])

  const selectedDateMenu = menus.find(
    (menu) => menu.date === selectedWeekDay?.format('YYYY-MM-DD')
  )
  const fixedMenuItems = selectedDateMenu?.menu_recipes || []

  const isDateInPast = selectedWeekDay.isBefore(
    moment.tz(tenantTz).startOf('day')
  )

  const visibleFixedMenuItems = fixedMenuItems.filter(
    (item) => !removedFixedItemIds.includes(item.recipe_id)
  )

  const excludedRecipeIds = [
    ...visibleFixedMenuItems.map((item) => item.recipe_id),
    ...droppedRecipes.map((item) => item.recipe_id)
  ]

  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('date', selectedDate.format('YYYY-MM-DD'))
    window.history.replaceState({}, '', url.toString())
  }, [selectedDate])

  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('date', selectedWeekDay.format('YYYY-MM-DD'))
    window.history.replaceState({}, '', url.toString())
  }, [selectedWeekDay])

  const handlePreviousWeek = () => {
    if (!confirmNavigationIfUnsaved()) return
    setSelectedDate((prev) => prev.clone().subtract(1, 'week'))
    setSelectedWeekDay((prev) => prev.clone().subtract(1, 'week'))
    resetChangesState()
  }

  const handleNextWeek = () => {
    if (!confirmNavigationIfUnsaved()) return
    setSelectedDate((prev) => prev.clone().add(1, 'week'))
    setSelectedWeekDay((prev) => prev.clone().add(1, 'week'))
    resetChangesState()
  }

  const handleDateChange = (newDate: Moment) => {
    if (!newDate.isSame(selectedWeekDay, 'day')) {
      if (!confirmNavigationIfUnsaved()) return
      resetChangesState()
    }
    setSelectedDate(newDate)
    setSelectedWeekDay(newDate)
  }

  const handleDayCardClick = (day: Moment) => {
    if (!day.isSame(selectedWeekDay, 'day')) {
      if (!confirmNavigationIfUnsaved()) return
      resetChangesState()
    }
    setSelectedWeekDay(day)
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result
    if (!destination) return

    if (destination.droppableId !== 'menu-dropzone') return

    if (source.droppableId === 'menu-dropzone') {
      if (!draggableId.startsWith('dropped-')) return

      const droppedId = parseInt(draggableId.replace('dropped-', ''), 10)
      const movedItem = droppedRecipes.find((r) => r.id === droppedId)
      if (!movedItem) return

      const newPosition = destination.index

      // Recalculate positions for all dropped items based on the move
      const updatedRecipes = droppedRecipes.map((r) => {
        if (r.id === droppedId) {
          return { ...r, position: newPosition }
        }
        // Shift other items' positions as needed
        if (movedItem.position < newPosition) {
          // moving down: items between old and new position shift up
          if (r.position > movedItem.position && r.position <= newPosition) {
            return { ...r, position: r.position - 1 }
          }
        } else {
          // moving up: items between new and old position shift down
          if (r.position >= newPosition && r.position < movedItem.position) {
            return { ...r, position: r.position + 1 }
          }
        }
        return r
      })

      setDroppedRecipes(updatedRecipes)
      setHasUnsavedChanges(true)
      return
    }

    const draggedRecipe = JSON.parse(draggableId)

    const existingIndex = droppedRecipes.findIndex(
      (r) => r.recipe_id === draggedRecipe.id
    )

    if (existingIndex >= 0) {
      const updatedRecipes = [...droppedRecipes]
      updatedRecipes[existingIndex].quantity += 1
      setDroppedRecipes(updatedRecipes)
      setHasUnsavedChanges(true)
    } else {
      const newPosition = destination.index

      // Shift positions of existing dropped items that are at or after the insert position
      const updatedRecipes = droppedRecipes.map((r) =>
        r.position >= newPosition ? { ...r, position: r.position + 1 } : r
      )

      const newDroppedRecipe: DroppedRecipe = {
        id: Date.now(), // temporary unique ID
        recipe_id: draggedRecipe.id,
        name: draggedRecipe.name,
        image_url: draggedRecipe.image_url,
        quantity: 1,
        position: newPosition
      }

      setDroppedRecipes([...updatedRecipes, newDroppedRecipe])
      setHasUnsavedChanges(true)
    }
  }

  const handleRemoveDroppedRecipe = (recipeId: number) => {
    setDroppedRecipes(droppedRecipes.filter((r) => r.recipe_id !== recipeId))
    setHasUnsavedChanges(true)
  }

  const handleRemoveFixedItem = (recipeId: number) => {
    setRemovedFixedItemIds((prev) => [...prev, recipeId])
    setHasUnsavedChanges(true)
  }

  const handleUpdateDroppedQuantity = (
    recipeId: number,
    newQuantity: number
  ) => {
    if (newQuantity < 1) return
    const updatedRecipes = droppedRecipes.map((r) =>
      r.recipe_id === recipeId ? { ...r, quantity: newQuantity } : r
    )
    setDroppedRecipes(updatedRecipes)
    setHasUnsavedChanges(true)
  }

  const handleUpdateFixedQuantity = (recipeId: number, newQuantity: number) => {
    if (newQuantity < 1) return
    setFixedItemQuantityOverrides((prev) => ({
      ...prev,
      [recipeId]: newQuantity
    }))
    setHasUnsavedChanges(true)
  }

  const showFlashMessage = (type: 'success' | 'danger', message: string) => {
    setFlashMessage({ type, message })
    setTimeout(() => setFlashMessage(null), 3000)
  }

  const handleCopyMenu = () => {
    // Combine fixed menu items and dropped recipes into a unified list for copying
    const allItems = [
      ...fixedMenuItems.map((item) => ({
        recipe_id: item.recipe_id,
        name: item.recipe_name,
        image_url: item.image_url,
        quantity: fixedItemQuantityOverrides[item.recipe_id] ?? item.amount
      })),
      ...droppedRecipes.map((item) => ({
        recipe_id: item.recipe_id,
        name: item.name,
        image_url: item.image_url,
        quantity: item.quantity
      }))
    ]
    setCopiedMenuItems(allItems)
  }

  const handlePasteMenu = () => {
    if (!copiedMenuItems) return

    // Filter out items that already exist in the current menu (fixed items)
    const existingRecipeIds = fixedMenuItems.map((item) => item.recipe_id)
    const itemsToPaste = copiedMenuItems.filter(
      (item) => !existingRecipeIds.includes(item.recipe_id)
    )

    // Also filter out items that are already in droppedRecipes
    const droppedRecipeIds = droppedRecipes.map((item) => item.recipe_id)
    const newItems = itemsToPaste.filter(
      (item) => !droppedRecipeIds.includes(item.recipe_id)
    )

    // Create new dropped recipes from pasted items
    const startPosition = fixedMenuItems.length + droppedRecipes.length
    const newDroppedRecipes: DroppedRecipe[] = newItems.map((item, idx) => ({
      id: Date.now() + idx,
      recipe_id: item.recipe_id,
      name: item.name,
      image_url: item.image_url,
      quantity: item.quantity,
      position: startPosition + idx
    }))

    setDroppedRecipes([...droppedRecipes, ...newDroppedRecipes])
    if (newDroppedRecipes.length > 0) {
      setHasUnsavedChanges(true)
    }
  }

  const handleSaveMenu = () => {
    const recipes = [
      ...fixedMenuItems
        .filter((item) => !removedFixedItemIds.includes(item.recipe_id))
        .map((item) => ({
          id: item.recipe_id,
          amount: fixedItemQuantityOverrides[item.recipe_id] ?? item.amount
        })),
      ...droppedRecipes.map((item) => ({
        id: item.recipe_id,
        amount: item.quantity
      }))
    ]

    if (recipes.length === 0) {
      showFlashMessage('danger', 'Please add at least one recipe to the menu')
      return
    }

    const dateString = selectedWeekDay.format('YYYY-MM-DD')

    setLoading(true)
    axios
      .post(merge_api_internal_menus_path(), {
        menu: {
          id: selectedDateMenu?.id,
          dates: [dateString],
          recipes
        }
      })
      .then(() => {
        showFlashMessage(
          'success',
          `Menu successfully ${selectedDateMenu?.id ? 'updated' : 'created'}`
        )
        resetChangesState()

        return fetchMenu()
      })
      .then(() => {
        setLoading(false)
      })
      .catch((error) => {
        showFlashMessage(
          'danger',
          error.response?.data?.error || 'Error saving menu'
        )
        setLoading(false)
      })
  }

  return (
    <LocalizationContext.Provider
      value={localization_context || DEFAULT_LOCALIZATION_CONTEXT}
    >
      {flashMessage && (
        <FlashNotification
          type={flashMessage.type}
          message={flashMessage.message}
          onClose={() => setFlashMessage(null)}
        />
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="menu-container">
          <div className="menu-week-days-row">
            <div className="menu-navigation-search">
              <WeekPicker
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                onPreviousWeek={handlePreviousWeek}
                onNextWeek={handleNextWeek}
              />

              <MenuSearchBar
                className="menu-search-bar"
                placeholder="search menu..."
                onQueryChange={setMenuSearchQuery}
              />
            </div>
            <div className="spacer" />
          </div>

          <div className="menu-week-days-row">
            <WeekView
              selectedDate={selectedDate}
              selectedWeekDay={selectedWeekDay}
              onDayClick={handleDayCardClick}
            />
            <MenuSearchBar
              className="recipes-search-bar"
              placeholder="search recipes..."
              onQueryChange={setRecipesSearchQuery}
            />
          </div>

          <div className="menu-content-row">
            <MenuList
              selectedDate={selectedWeekDay}
              searchQuery={menuSearchQuery}
              droppedRecipes={droppedRecipes}
              fixedMenuItems={visibleFixedMenuItems}
              fixedItemQuantityOverrides={fixedItemQuantityOverrides}
              onRemoveDroppedRecipe={handleRemoveDroppedRecipe}
              onRemoveFixedItem={handleRemoveFixedItem}
              onUpdateDroppedQuantity={handleUpdateDroppedQuantity}
              onUpdateFixedQuantity={handleUpdateFixedQuantity}
              onCopyMenu={handleCopyMenu}
              onPasteMenu={handlePasteMenu}
              onSaveMenu={handleSaveMenu}
              hasCopiedItems={
                copiedMenuItems !== null && copiedMenuItems.length > 0
              }
              loading={loading}
              menuLoading={menuLoading}
              isDateInPast={isDateInPast}
              readOnly={!canEdit}
            />
            <RecipeList
              searchInput={recipesSearchQuery}
              excludedRecipeIds={excludedRecipeIds}
              dragDisabled={!canEdit}
            />
          </div>
        </div>
      </DragDropContext>
    </LocalizationContext.Provider>
  )
}

export default NewMenu
